import { unref } from 'vue'
import { useCore } from '@/composables/core/index.js'
import { useChat, createChat } from '@/composables/chat/index.js'
import { useLogger } from '@booksy-com/vue-tools'
import { equals } from 'ramda'
import { useDeeplinksService } from '@/composables/deeplinks/index.js'
import { useEmitter } from '@/composables/emitter/index.js'
import { useExperiments } from '@/composables/experiments/index.js'
import flags from '@/lib/flags.js'
import {
  booksyBillingRedirect,
  deeplinkRedirect,
  defaultRouteRedirect,
  gdprRedirect,
  nativeSubscriptionRedirect,
  onboardingFinishedRedirect,
  onboardingRedirect,
  paymentMethodInfoRedirect,
  richTextEditorRedirect,
  targetUrlRedirect,
  deleteAccountRedirect,
} from './redirects/index.js'

import { useRedirection } from './redirection.js'

const logger = useLogger('default-redirect')

const ORDERED_REDIRECTS = [
  richTextEditorRedirect,
  onboardingRedirect,
  deleteAccountRedirect,
  booksyBillingRedirect,
  paymentMethodInfoRedirect,
  nativeSubscriptionRedirect,
  gdprRedirect,
  deeplinkRedirect,
  targetUrlRedirect,
  onboardingFinishedRedirect,
]

const defaultRedirect = async ({ onboardingFinished } = { onboardingFinished: false }) => {
  const { store, router } = useCore()
  const emitter = useEmitter()
  const { execute } = useDeeplinksService()
  const experiments = useExperiments()

  logger.info('default redirect started')

  // redirection query
  const redirection = useRedirection().get()
  const _currentRoute = unref(router.currentRoute)
  const route = {
    ..._currentRoute,
    params: {
      ..._currentRoute.params,
      ...(redirection && { redirection }),
    },
  }
  const query = route.params.redirection?.query || {}
  const params = route.params

  // delete redirection to current route
  if (params.redirection && route.name
    && (equals(`/${params.redirection.path}`, route.path)
    || equals(params.redirection.name, route.name))
  ) {
    useRedirection().clear()
    delete params.redirection
  }

  // IMPORTANT!!!
  // do not add anything unnecessary here
  const context = {
    route,
    query,
    params,
    router,
    store,
    emitter,
    logger,
    executeDeeplink: execute,
    defaultRedirect,
    // TODO to be replaced with storage composable
    flags,
    // TODO stuff to be removed from this context
    useChat,
    createChat,
    onboardingFinished,
    experiments,
  }

  for (const redirect of ORDERED_REDIRECTS) {
    const result = await redirect(context)
    if (result) {
      await router.isReady()
      return result
    }
  }

  useRedirection().clear()
  return defaultRouteRedirect(context)
}

export function useDefaultRedirect () {
  return defaultRedirect
}
